import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Pagination,
  CircularProgress,
  Alert,
  Paper,
  List,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Book as BookIcon,
  Description as ResourceIcon,
  Visibility as ViewIcon,
  CalendarToday as DateIcon,
  Person as AuthorIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSearchStore } from '../../stores/useSearchStore';
import type { SearchResult } from '../../types/search';
import ResourceViewer from '../../components/ResourceViewer';
import ResourceDownload from '../../components/ResourceDownload';

interface SearchResultsProps {
  onResultClick?: (result: SearchResult) => void;
}

const ResultTypeIcon = ({ type }: { type: string }) => {
  return type === 'book' ? <BookIcon /> : <ResourceIcon />;
};

const ResultCard: React.FC<{
  result: SearchResult;
  onClick?: () => void;
  onView?: (resource: any) => void;
  onDownload?: (resource: any) => void;
}> = ({ result, onClick, onView, onDownload }) => {
  const { searchRequest, setSearchRequest, search } = useSearchStore();

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Chưa có ngày';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Ngày không hợp lệ';
      }
      return date.toLocaleDateString('vi-VN');
    } catch (error) {
      return 'Ngày không hợp lệ';
    }
  };

  const handleTagClick = async (tag: string) => {
    const currentTags = searchRequest.tags || [];
    const newTags = currentTags.includes(tag)
      ? currentTags.filter(t => t !== tag)
      : [...currentTags, tag];

    setSearchRequest({
      tags: newTags,
      page: 1
    });

    await search();
  };

  const handleFileTypeClick = async (fileType: string) => {
    setSearchRequest({
      fileType: fileType,
      page: 1
    });
    await search();
  };

  const handleAuthorClick = async (author: string) => {
    setSearchRequest({
      author: author,
      page: 1
    });
    await search();
  };

  const handleDateClick = async (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    setSearchRequest({
      startDate: dateStr,
      endDate: dateStr,
      page: 1
    });
    await search();
  };

  return (
    <Card
      sx={{
        mb: 2,
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease-in-out'
        }
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ mt: 0.5 }}>
            <ResultTypeIcon type={result.resultType} />
          </Box>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
              {result.title}
            </Typography>

            {result.description && (
              <Typography variant="body2" color="text.secondary" sx={{
                mb: 1,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}>
                {result.description}
              </Typography>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {result.author && (
                <Chip
                  icon={<AuthorIcon />}
                  label={result.author}
                  size="small"
                  variant={searchRequest.author === result.author ? "filled" : "outlined"}
                  color={searchRequest.author === result.author ? "primary" : "default"}
                  clickable
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAuthorClick(result.author!);
                  }}
                />
              )}

              {result.fileType && (
                <Chip
                  label={result.fileType.toUpperCase()}
                  size="small"
                  variant={searchRequest.fileType === result.fileType ? "filled" : "outlined"}
                  color="primary"
                  clickable
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFileTypeClick(result.fileType!);
                  }}
                />
              )}

              {result.dateCreated && (
                <Chip
                  icon={<DateIcon />}
                  label={formatDate(result.dateCreated)}
                  size="small"
                  variant="outlined"
                  clickable
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDateClick(result.dateCreated!);
                  }}
                />
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Tooltip title="Xem">
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onView?.(result);
                }}
              >
                <ViewIcon />
              </IconButton>
            </Tooltip>

            <ResourceDownload
              resource={result as any}
              variant="icon"
              size="small"
              onDownloadComplete={onDownload}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export const SearchResults: React.FC<SearchResultsProps> = ({ onResultClick }) => {
  const navigate = useNavigate();
  const {
    searchResponse,
    isLoading,
    error,
    searchRequest,
    setSearchRequest,
    search
  } = useSearchStore();

  const [viewingResource, setViewingResource] = React.useState<any>(null);
  const [viewerOpen, setViewerOpen] = React.useState(false);

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setSearchRequest({ page });
    search();
  };

  const handleView = (result: SearchResult) => {
    if (result.resultType === 'book') {
      navigate(`/books/${result.id}`);
    } else {
      setViewingResource({
        ...result,
        fileName: result.title, // Fallback for viewer
      });
      setViewerOpen(true);
    }
  };

  const handleDownload = (resource: any) => {
    // Analytics or refresh logic if needed
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!searchResponse) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Nhập từ khóa để tìm kiếm tài nguyên
        </Typography>
      </Paper>
    );
  }

  if (searchResponse.results.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Không tìm thấy kết quả
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Hãy thử điều chỉnh tiêu chí tìm kiếm
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Kết Quả Tìm Kiếm
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Tìm thấy {searchResponse.totalCount} kết quả
          {searchResponse.totalPages > 1 && (
            <span> (Trang {searchResponse.page} của {searchResponse.totalPages})</span>
          )}
        </Typography>
      </Box>

      <List>
        {searchResponse.results.map((result, index) => (
          <Box key={`${result.resultType}-${result.id}-${index}`}>
            <ResultCard
              result={result}
              onClick={() => onResultClick?.(result)}
              onView={handleView}
              onDownload={handleDownload}
            />
          </Box>
        ))}
      </List>

      {searchResponse.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={searchResponse.totalPages}
            page={searchResponse.page}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}

      {viewingResource && (
        <ResourceViewer
          resource={viewingResource}
          open={viewerOpen}
          onClose={() => {
            setViewerOpen(false);
            setViewingResource(null);
          }}
          onDownload={handleDownload}
        />
      )}
    </Box>
  );
};
